import { FC, ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { getBebyggelseobjektUtkast, postInventeringUtkast } from 'store/bebyggelseobjektUtkastSlice';
import { clearFeatureInfoByggnader } from 'store/featureInfoByggnaderSlice';
import { clearFeatureInfoInventeringar } from 'store/featureInfoInventeringarSlice';
import { clearClickedActiveFeatures } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';
import { clearUtkastState, patchUtkastSammansattReferens } from 'store/utkastSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';

import { RaaSpinner } from 'raa-react';

import { featureInfoPopupOverlay } from 'components/map/Layers';
import { Colors } from 'components/map/Styles';
import {
  buildingCircle,
  churchCircle,
  crownCircle,
  defaultIcon,
  hourglassCircleSvg,
  inventeringCircle,
  penCircleSvg
} from 'components/map/icons';

import { BebyggelseobjektUtkastIndex, HTTP_STATUS, Inventering } from 'models';
import { FeatureInfoByggnaderResponse } from 'models/apiModels/FeatureInfoByggnaderResponse';

import { ActiveFeatureType, BebyggelseklassEnum, BebyggelseobjektgruppEnum, MapModeEnum, SkyddstypEnum } from 'enums';

import { Feature } from 'ol';
import GeoJSON from 'ol/format/GeoJSON';
import { Point } from 'ol/geom';

import { pointsWithinPolygon } from '@turf/turf';

import { useReserveBebyggelseutkastMutation } from 'api/endpoints/reservationSlice';
import classNames from 'classnames';
import { getInventeringDateText } from 'utilities/getInventeringDateText';

interface PopupItem {
  id: string;
  title: ReactNode | string;
  description1: string;
  description2: string;
  url: string;
  icon: string;
  disabled: boolean;
  type: ActiveFeatureType;
  skyddstyp: SkyddstypEnum | undefined;
  bebyggelseklass: BebyggelseklassEnum;
}

const FeatureInfoPopup: FC = (): ReactElement => {
  const featureInfoPopupRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const [popupItems, setPopupItems] = useState<PopupItem[]>([]);

  const clickedActiveFeatures = useAppSelector((state: RootState) => state.map.clickedActiveFeatures);

  const featureInfoByggnaderCoordinates = useAppSelector((state: RootState) => state.featureInfoByggnader.requestedAt);
  const featureInfoByggnader = useAppSelector((state: RootState) => state.featureInfoByggnader.data);

  const aktivitet = useAppSelector((state: RootState) => state.aktivitet.data);
  const inventering = aktivitet?.inventering;
  const skyddsklassning = aktivitet?.skyddsklassning;
  const mapMode = useAppSelector((state: RootState) => state.map.mapMode);

  const { data: bebyggelseobjektUtkast } = useAppSelector((state: RootState) => state.bebyggelseobjektUtkast);
  const featureInfoInventeringarCoordinates = useAppSelector(
    (state: RootState) => state.featureInfoInventeringar.requestedAt
  );

  const featureInfoInventeringar = useAppSelector((state: RootState) => state.featureInfoInventeringar.data);

  if (featureInfoPopupRef.current) {
    featureInfoPopupOverlay.setElement(featureInfoPopupRef.current);
  }

  const isWithinInventeringsomradeMemo = useMemo(() => {
    if (!featureInfoByggnader || !inventering) return false;

    return isWithinInventeringsomrade(featureInfoByggnader, inventering);
  }, [featureInfoByggnader, inventering]);

  const closePopup = useCallback(() => {
    featureInfoPopupOverlay.setPosition(undefined);
    dispatch(clearFeatureInfoByggnader(undefined));
    dispatch(clearFeatureInfoInventeringar(undefined));
    dispatch(clearClickedActiveFeatures());
  }, [dispatch]);

  const showPopup = useCallback(() => {
    if (featureInfoByggnaderCoordinates) {
      featureInfoPopupOverlay.setPosition(featureInfoByggnaderCoordinates);
    } else if (featureInfoInventeringarCoordinates) {
      featureInfoPopupOverlay.setPosition(featureInfoInventeringarCoordinates);
    } else if (clickedActiveFeatures?.activeFeaturesRequestedAt) {
      featureInfoPopupOverlay.setPosition(clickedActiveFeatures?.activeFeaturesRequestedAt);
    }
  }, [
    featureInfoByggnaderCoordinates,
    featureInfoInventeringarCoordinates,
    clickedActiveFeatures?.activeFeaturesRequestedAt
  ]);

  useEffect(() => {
    if (featureInfoPopupRef.current) {
      const stopPropagationForEvents = ['touchstart', 'touchend', 'touchmove', 'onpointerdown', 'onpointermove'];
      stopPropagationForEvents.forEach((event) =>
        featureInfoPopupRef.current?.addEventListener(event, (event: Event) => event.stopPropagation(), {
          passive: false
        })
      );
    }
  }, []);

  useEffect(() => {
    closePopup();
  }, [closePopup, bebyggelseobjektUtkast]);

  useEffect(() => {
    if (
      featureInfoByggnaderCoordinates ||
      featureInfoInventeringarCoordinates ||
      clickedActiveFeatures?.activeFeaturesRequestedAt
    ) {
      showPopup();
    } else {
      closePopup();
    }
  }, [
    closePopup,
    featureInfoByggnaderCoordinates,
    featureInfoInventeringarCoordinates,
    clickedActiveFeatures,
    showPopup
  ]);

  const resolveUrl = (type: ActiveFeatureType, id: string | null) => {
    switch (type) {
      case ActiveFeatureType.INVENTERING:
        return `/aktivitet/${id}`;
      case ActiveFeatureType.INVENTERING_PRELIMINAR:
        return `/inventering/${id}/preliminar`;
      case ActiveFeatureType.UTKAST_REDIGERBAR:
        return `/utkast/${id}/redigera`;
      case ActiveFeatureType.UTKAST_INSKICKAD:
        return `/utkast/${id}`;
      case ActiveFeatureType.PUBLICERAD:
        return `/bebyggelseobjekt/${id}`;
      default:
        return '';
    }
  };

  const sortItems = (items: PopupItem[]) => {
    const typOrder = [
      ActiveFeatureType.UTKAST_REDIGERBAR,
      ActiveFeatureType.PUBLICERAD,
      ActiveFeatureType.INVENTERING,
      ActiveFeatureType.BYGGNADINDEX,
      ActiveFeatureType.UNKNOWN
    ];
    const skyddsOrder = [
      SkyddstypEnum.STATLIGT_BYGGNADSMINNE,
      SkyddstypEnum.BYGGNADSMINNE,
      SkyddstypEnum.KYRKLIGT_KULTURMINNE,
      SkyddstypEnum.IDENTIFIERAT_KULTURVARDE,
      SkyddstypEnum.ANMALNINGSPLIKT,
      undefined
    ];
    return items.sort((a, b) => {
      const typA = typOrder.indexOf(a.type);
      const typB = typOrder.indexOf(b.type);
      if (typA > typB) return 1;
      if (typA < typB) return -1;

      const skyddA = skyddsOrder.indexOf(a.skyddstyp);
      const skyddB = skyddsOrder.indexOf(b.skyddstyp);
      if (skyddA > skyddB) return 1;
      if (skyddA < skyddB) return -1;

      return 0;
    });
  };

  useEffect(() => {
    const aktivItems: PopupItem[] =
      clickedActiveFeatures?.activeFeatures.map((feature) => {
        return {
          id: feature.id || '',
          description1: feature.description1 || '',
          description2: feature.description2 || '',
          title: (
            <span>
              {feature.title !== feature.identitetsnummer && feature.title + ' '}
              <span className="tw-italic">({feature.identitetsnummer})</span>
            </span>
          ),
          url: resolveUrl(feature.type, feature.id),
          disabled: false,
          icon: resolveIconForActiveLayer(feature.type, feature.skyddstyp),
          type: feature.type,
          skyddstyp: feature.skyddstyp
        } as PopupItem;
      }) || [];

    const publiceradByggnadItems: PopupItem[] =
      featureInfoByggnader?.features
        .filter((b) => !aktivItems.length || !aktivItems.some((item) => b.properties.id === item.id))
        .map((feature) => {
          const props = feature.properties;
          const getType = () => {
            return props.has_utkast
              ? ActiveFeatureType.UTKAST_REDIGERBAR
              : props.havt_skydd
                ? ActiveFeatureType.HAVT_SKYDD
                : ActiveFeatureType.PUBLICERAD;
          };
          return {
            id: props.id,
            description1: [BebyggelseklassEnum.SAMMANSATT, BebyggelseklassEnum.OMRADE].includes(props.bebyggelseklass)
              ? props.primart_andamal
              : props.fastighet,
            description2: `${props.skyddstyp_namn} ${props.havt_skydd ? ' (HÄVT)' : ''}`,
            title: (
              <span>
                {props.titel} <span className="tw-italic">({props.bebyggelsenummer})</span>
              </span>
            ),
            url: `/bebyggelseobjekt/${props.id}`,
            disabled: false,
            icon: resolveIconForFeatureInfoLayer(props.skyddstyp, props.havt_skydd),
            type: getType(),
            skyddstyp: props.skyddstyp,
            bebyggelseklass: props.bebyggelseklass
          };
        }) || [];

    const inventeringItems: PopupItem[] =
      featureInfoInventeringar?.features
        .filter((b) => !aktivItems.length || !aktivItems.some((item) => b.properties.aktivitet_id === item.id))
        .map((feature) => {
          const props = feature.properties;
          return {
            id: props.aktivitet_id,
            description1: getInventeringDateText(props.start_ar, props.slut_ar),
            description2: `Inventering`,
            title: (
              <span>
                {props.titel} <span className="tw-italic">({props.aktivitetsnummer})</span>
              </span>
            ),
            url: `/aktivitet/${props.aktivitet_id}`,
            disabled: false,
            icon: encodeURIComponent(inventeringCircle(Colors.RAA_BLUE, Colors.WHITE)),
            type: ActiveFeatureType.INVENTERING
          } as PopupItem;
        }) || [];

    setPopupItems(sortItems(aktivItems).concat(sortItems(publiceradByggnadItems).concat(inventeringItems)));
  }, [clickedActiveFeatures, featureInfoByggnader, featureInfoInventeringar]);

  return (
    <div
      ref={featureInfoPopupRef}
      className={classNames(
        'ol-popup tw-left-[-16em] tw-flex tw-max-h-[30rem] tw-min-w-[320px] tw-flex-col tw-rounded-md tw-shadow-md before:tw-left-[16em] after:tw-left-[16em] md:tw-left-[-26em] md:tw-min-w-[29rem] before:md:tw-left-[26em] after:md:tw-left-[26em]',
        {
          'tw-hidden': !popupItems.length
        }
      )}
    >
      <button
        className="ol-popup-closer tw-text-size-1.313 tw-text-raa-orange"
        type="button"
        aria-label="stäng popup"
        onClick={closePopup}
      />

      <div className="tw-flex tw-flex-col tw-overflow-y-hidden tw-border-b tw-border-raa-gray-3 tw-pt-0">
        <FeatureInfoTopBar featureInfoByggnader={featureInfoByggnader} />
        <div className="tw-overflow-y-auto">
          <ul>
            {popupItems.map((feature) => {
              const isInInventering = !!inventering && (!featureInfoByggnader || isWithinInventeringsomradeMemo);
              const alreadyInSammansatt =
                !!skyddsklassning &&
                MapModeEnum.ADD_UTKAST_TO_SAMMANSATT &&
                (bebyggelseobjektUtkast?.some((x) => feature.id === x.utkast_id) || false);

              const allowAddAction =
                ((mapMode === MapModeEnum.ADD_UTKAST_TO_INVENTERING && isInInventering) ||
                  mapMode === MapModeEnum.ADD_UTKAST_TO_ENKELINVENTERING ||
                  (mapMode === MapModeEnum.ADD_UTKAST_TO_SAMMANSATT && !alreadyInSammansatt)) &&
                feature.bebyggelseklass !== BebyggelseklassEnum.SAMMANSATT;

              return mapMode === MapModeEnum.ADD_UTKAST_TO_INVENTERING ||
                mapMode === MapModeEnum.ADD_UTKAST_TO_ENKELINVENTERING ||
                mapMode === MapModeEnum.ADD_UTKAST_TO_SAMMANSATT ? (
                <li
                  key={feature.id}
                  className={classNames('tw-flex tw-justify-between tw-border-b tw-border-raa-gray-3 tw-p-4 tw-px-4', {
                    'hover:tw-pointer-cursor hover:tw-bg-raa-gray-3':
                      allowAddAction && feature.type === ActiveFeatureType.PUBLICERAD,
                    'tw-opacity-50': !allowAddAction || feature.type !== ActiveFeatureType.PUBLICERAD
                  })}
                >
                  <FeatureContent feature={feature} />
                  <FeatureActions
                    feature={feature}
                    allowAddAction={allowAddAction}
                    mapMode={mapMode}
                    closePopup={closePopup}
                  />
                </li>
              ) : (
                <li
                  className="tw-flex tw-justify-between tw-border-b tw-border-raa-gray-3 hover:tw-cursor-pointer hover:tw-bg-raa-gray-3"
                  key={feature.id}
                >
                  <Link onClick={closePopup} to={feature.url} className="tw-flex tw-w-full tw-p-4">
                    <FeatureContent feature={feature} />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

const FeatureActions: FC<{
  feature: PopupItem;
  allowAddAction: boolean | undefined;
  mapMode?: string;
  closePopup: () => void;
}> = ({ feature, allowAddAction, mapMode, closePopup }): ReactElement => {
  const dispatch = useAppDispatch();
  const bebyggelseobjektUtkast = useAppSelector((state: RootState) => state.bebyggelseobjektUtkast.data);
  const { requestStatus: utkastRequestStatus, requestType } = useAppSelector(
    (state: RootState) => state.bebyggelseobjektUtkast
  );

  const { data: aktivitetData } = useAppSelector((state: RootState) => state.aktivitet);
  const aktivitet = aktivitetData?.inventering ?? aktivitetData?.enkelinventering ?? aktivitetData?.skyddsklassning;

  const [patchReservation, results] = useReserveBebyggelseutkastMutation();
  const { isSuccess: reserveraIsSuccess } = results;
  const reserveraIsSuccessRef = useRef<boolean>();
  reserveraIsSuccessRef.current = reserveraIsSuccess;

  const getSammansattObjekt = useCallback(() => {
    return bebyggelseobjektUtkast?.find(
      (x: BebyggelseobjektUtkastIndex) => x.bebyggelseobjektgrupp === BebyggelseobjektgruppEnum.SAMMANSATT
    );
  }, [bebyggelseobjektUtkast]);

  const createUtkast = async (utkastId: string, aktivitetId: string) => {
    try {
      await dispatch(
        postInventeringUtkast({
          bebyggelseobjektBefintligPost: {
            aktivitet_id: aktivitetId,
            bebyggelseobjekt_identiteter: [utkastId]
          },
          ignoreHttpStatus: [HTTP_STATUS.UNPROCESSABLE_ENTITY]
        })
      ).unwrap();
      closePopup();
      dispatch(clearUtkastState());
      dispatch(getBebyggelseobjektUtkast(aktivitetId));
    } catch (error: any) {
      return Promise.reject(error);
    }
    return Promise.resolve();
  };

  const createIngaendeUtkast = async (utkastId: string, aktivitetId: string) => {
    const sammansattObjekt = getSammansattObjekt();

    if (sammansattObjekt) {
      try {
        await patchReservation({ id: sammansattObjekt?.utkast_id, reserve: true }).unwrap();

        if (reserveraIsSuccessRef.current) {
          await dispatch(
            patchUtkastSammansattReferens({
              utkast_id: sammansattObjekt?.utkast_id,
              body: {
                bebyggelseobjekt_id: [utkastId]
              }
            })
          ).unwrap();
          closePopup();
          dispatch(clearUtkastState());
          patchReservation({ id: sammansattObjekt?.utkast_id, reserve: false });
          dispatch(getBebyggelseobjektUtkast(aktivitetId));
        }
      } catch (error: any) {
        return Promise.reject(error);
      }
    }
    return Promise.resolve();
  };

  return allowAddAction && feature.type === ActiveFeatureType.PUBLICERAD ? (
    <button
      type="button"
      className="raa-button raa-button-secondary tw-ml-auto tw-min-w-[8rem]"
      aria-label={`Skapa utkast för byggnad ${feature.title}`}
      disabled={(requestType === 'post' || requestType === 'patch') && utkastRequestStatus === 'pending'}
      onClick={() => {
        if (mapMode === MapModeEnum.ADD_UTKAST_TO_SAMMANSATT) {
          void createIngaendeUtkast(feature.id, aktivitet?.aktivitet_id || '');
        } else if (
          mapMode === MapModeEnum.ADD_UTKAST_TO_INVENTERING ||
          mapMode === MapModeEnum.ADD_UTKAST_TO_ENKELINVENTERING
        ) {
          void createUtkast(feature.id, aktivitet?.aktivitet_id || '');
        }
      }}
    >
      {((requestType === 'post' || requestType === 'patch') && utkastRequestStatus === 'pending' && (
        <RaaSpinner size={'1rem'} />
      )) ||
        'lägg till'}
    </button>
  ) : (
    <p className="tw-self-center tw-pt-2 tw-text-size-0.813">
      <span className="raa-icon raa-icon-lock-closed tw-p-2 tw-text-raa-gray-5"></span>

      {feature.type === ActiveFeatureType.UTKAST_REDIGERBAR ? (
        'Redigeras'
      ) : feature.type === ActiveFeatureType.UTKAST_INSKICKAD ? (
        'Inskickad'
      ) : (
        <></>
      )}
    </p>
  );
};

const FeatureContent: FC<{ feature: PopupItem }> = ({ feature }): ReactElement => {
  return (
    <div className="tw-flex tw-flex-grow">
      <img
        src={`data:image/svg+xml;utf8,${feature.icon}`}
        className="tw-h-auto tw-w-10"
        alt="Ikon för bebyggelseobjekt"
      />
      <div className="tw-pl-4">
        <p className="tw-m-0 tw-font-semibold">{feature.title}</p>
        {feature.description1 && <p>{feature.description1}</p>}
        {feature.description2 && <p>{feature.description2}</p>}
      </div>
    </div>
  );
};

const FeatureInfoTopBar: FC<{
  featureInfoByggnader: FeatureInfoByggnaderResponse | undefined;
}> = ({ featureInfoByggnader }): ReactElement => {
  const mapMode = useAppSelector((state: RootState) => state.map.mapMode);
  const aktivitet = useAppSelector((state: RootState) => state.aktivitet.data);
  const inventering = aktivitet?.inventering;
  const skyddsklassning = aktivitet?.skyddsklassning;
  const isWithinInventeringsomradeMemo = useMemo(() => {
    if (!featureInfoByggnader || !inventering) return false;

    return isWithinInventeringsomrade(featureInfoByggnader, inventering);
  }, [featureInfoByggnader, inventering]);

  return (
    <div className="tw-min-h-[2rem] tw-border-b tw-border-raa-gray-3 tw-p-2">
      {mapMode === MapModeEnum.ADD_UTKAST_TO_INVENTERING && featureInfoByggnader && inventering && (
        <p className="tw-justify-center tw-font-semibold">
          {isWithinInventeringsomradeMemo ? 'Välj objekt att skapa utkast från:' : 'Utanför inventeringsytan'}
        </p>
      )}
      {mapMode === MapModeEnum.ADD_UTKAST_TO_SAMMANSATT && featureInfoByggnader && skyddsklassning && (
        <>
          <p className="tw-justify-center tw-font-semibold">Lägg till objekt:</p>
        </>
      )}
    </div>
  );
};

const isWithinInventeringsomrade = (byggnader: FeatureInfoByggnaderResponse, inventering: Inventering) => {
  if (!byggnader && !inventering) return false;

  const byggnaderFeatures = new GeoJSON().readFeatures(byggnader) as Array<Feature<Point>>;
  const inventeringFeatures = new GeoJSON().readFeatures(inventering.inventeringsomrade);

  return byggnaderFeatures.some((byggnadFeature) => {
    return inventeringFeatures.some((inventeringFeature) => {
      const geoJson = new GeoJSON();

      const pointsInsidePolygon = pointsWithinPolygon(
        geoJson.writeFeatureObject(byggnadFeature),
        geoJson.writeFeatureObject(inventeringFeature)
      );

      return pointsInsidePolygon.features.length > 0;
    });
  });
};

const resolveIconForActiveLayer = (type: ActiveFeatureType | undefined, skyddstyp: SkyddstypEnum | undefined) => {
  let icon = defaultIcon();

  switch (type) {
    case ActiveFeatureType.INVENTERING:
    case ActiveFeatureType.INVENTERING_PRELIMINAR:
      icon = inventeringCircle(Colors.TURQUOISE, Colors.RAA_BLUE);
      break;
    case ActiveFeatureType.UTKAST_REDIGERBAR:
      icon = penCircleSvg(Colors.TURQUOISE, Colors.RAA_BLUE);
      break;
    case ActiveFeatureType.UTKAST_INSKICKAD:
      icon = hourglassCircleSvg(Colors.TURQUOISE);
      break;
    case ActiveFeatureType.PUBLICERAD:
      switch (skyddstyp) {
        case SkyddstypEnum.KYRKLIGT_KULTURMINNE:
          icon = churchCircle(Colors.TURQUOISE, Colors.RAA_BLUE);
          break;
        case SkyddstypEnum.STATLIGT_BYGGNADSMINNE:
          icon = crownCircle(Colors.TURQUOISE, Colors.RAA_BLUE);
          break;
        case SkyddstypEnum.IDENTIFIERAT_KULTURVARDE:
        case SkyddstypEnum.BYGGNADSMINNE:
        case SkyddstypEnum.ANMALNINGSPLIKT:
        case SkyddstypEnum.FORSTORT_KULTURVARDE:
          icon = buildingCircle(Colors.TURQUOISE, Colors.RAA_BLUE);
          break;
      }
      break;
  }

  return encodeURIComponent(icon);
};

const resolveIconForFeatureInfoLayer = (skyddstyp: SkyddstypEnum, havtSkydd: boolean) => {
  let icon: string;
  switch (skyddstyp) {
    case SkyddstypEnum.IDENTIFIERAT_KULTURVARDE:
    case SkyddstypEnum.ANMALNINGSPLIKT:
      icon = buildingCircle(havtSkydd ? Colors.RAA_GRAY_5 : Colors.RAA_BLUE);
      break;
    case SkyddstypEnum.STATLIGT_BYGGNADSMINNE:
      icon = crownCircle(havtSkydd ? Colors.RAA_GRAY_5 : Colors.RAA_ORANGE);
      break;
    case SkyddstypEnum.BYGGNADSMINNE:
      icon = buildingCircle(havtSkydd ? Colors.RAA_GRAY_5 : Colors.RAA_ORANGE);
      break;
    case SkyddstypEnum.KYRKLIGT_KULTURMINNE:
      icon = churchCircle(havtSkydd ? Colors.RAA_GRAY_5 : Colors.RAA_ORANGE);
      break;
    default:
      // Detta ska aldrig kunna ske!
      icon = defaultIcon();
  }
  return encodeURIComponent(icon);
};

export default FeatureInfoPopup;
